import { EventrDate, IEventrActivity, SectionId } from "@eventr/common";
import { ColoursObj, TEventrIcons } from "@eventr/common-storybook";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { memo, useMemo } from "react";
import { routes } from "routing";
import { getCategoryProperties } from "utils";
import { useScreenTypeContext } from "../state/screenTypeProvider";
import { ActivityBookingInfo } from "./activityComponents/activityBookingInfo";
import { ActivityDate } from "./activityComponents/activityDate";
import { ActivityDescription } from "./activityComponents/activityDescription";
import { ActivityExtraInfo } from "./activityComponents/activityExtraInfo";
import { ActivityHeader } from "./activityComponents/activityHeader";
import { ActivityImage } from "./activityComponents/activityImage";
import { EventTimeRow } from "./activityComponents/eventTimeRow";
import { useGetCategory } from "./hooks/useGetCategory";
import { ObjectProvider } from "./providers/objectProvider";
import { BaseCard } from "./subComponents/baseCard";
import { DraftMessage } from "./subComponents/draftMessage";

interface IActivityCardProps {
  id?: string;
  data?: IEventrActivity;
  past?: boolean;
  date?: string | undefined;
}

/**
 * Memoised: this is the card an exhibitor list renders thousands of, and its description runs the
 * full markdown pipeline. `data` identity is kept stable by `ComponentFinder`'s memoised
 * `sanitisedData`, so the default shallow comparison is enough.
 */
export const ActivityCard = memo(function ActivityCard({ id, data, date }: IActivityCardProps) {
  const { screenType } = useScreenTypeContext();
  const { pushPath } = useGlobalRouter();

  const mainCategory = useGetCategory(data?.categories?.[0]);
  const defaultIconByType = useMemo(() => {
    if (data?.type === "stand") {
      return "Stand" as TEventrIcons;
    }
    return "Activity" as TEventrIcons;
  }, [data?.type]);
  const attachments = {
    notes: data?.additional_information,
    extra_docs: data?.documents,
    website: data?.website,
  };
  const isPast = useMemo(() => {
    if (data?.end) {
      return new EventrDate(data.end).getUnixMs < new Date().getTime();
    }
    if (!data?.end && data?.start) {
      return new EventrDate(data.start).getUnixMs < new Date().getTime();
    }
    return false;
  }, [data]);

  if (!data) return null;

  const activityExtraSection = <ActivityExtraInfo location={data.location} address={data.address} />;
  const activityTimes = <EventTimeRow start={data.start} end={data.end} />;

  return (
    <ObjectProvider past={isPast} _id={data._id} sectionID={SectionId.ACTIVITY.toString()} visibility={data.visibility}>
      <BaseCard
        onClick={() => {
          if (screenType === "edit") {
            return;
          }
          pushPath(routes.item.activity.build({ itemId: data._id }), { state: { screenType } });
        }}
        id={id}
        past={isPast}
      >
        {screenType === "upNext" || screenType === "personal" ? (
          <>
            <ActivityImage images={data.cardImages ?? []} isAdvance={data.isAdvance} />
            <ActivityHeader
              visibility={data.visibility}
              activityName={data.activity_name}
              label={data.label}
              labelTime={data.labelTime}
              start={data.start}
              end={data.end}
              icon={{
                image: mainCategory?.icon ?? defaultIconByType,
                colour: getCategoryProperties(data?.categories?.[0])
                  ? (getCategoryProperties(data?.categories?.[0])?.colour ?? "")
                  : (mainCategory?.colour ?? ColoursObj.core.secondary),
              }}
            />
            <ActivityDescription description={data.description ?? ""} />
            {activityTimes}
            {activityExtraSection}
          </>
        ) : screenType === "edit" ? (
          <>
            <ActivityDate
              name={data.activity_name}
              date={data.start}
              visibility={data.visibility}
              icon={(mainCategory?.icon ?? defaultIconByType) as TEventrIcons}
              iconColour={
                getCategoryProperties(data?.categories?.[0])
                  ? (getCategoryProperties(data?.categories?.[0])?.colour ?? "")
                  : (mainCategory?.colour ?? ColoursObj.core.secondary)
              }
            />
            {activityTimes}
            {activityExtraSection}
            <ActivityBookingInfo
              chatNames={[data.activity_name ?? ""]}
              attendess={data.attendees_list}
              attachments={attachments}
            />
          </>
        ) : screenType === "team" ? (
          <>
            <ActivityImage images={data.cardImages ?? []} isAdvance={data.isAdvance} />
            <ActivityHeader
              visibility={data.visibility}
              activityName={data.activity_name}
              start={data.start}
              end={data.end}
              date={date}
              icon={{
                image: mainCategory?.icon ?? defaultIconByType,
                colour: getCategoryProperties(data?.categories?.[0])
                  ? (getCategoryProperties(data?.categories?.[0])?.colour ?? "")
                  : (mainCategory?.colour ?? ColoursObj.core.secondary),
              }}
            />
            {activityTimes}
            {activityExtraSection}
            <ActivityBookingInfo
              chatNames={[data.activity_name ?? ""]}
              attendess={data.attendees_list}
              attachments={attachments}
            />
          </>
        ) : screenType === "teamSearch" || screenType === "teamMember" ? (
          <>
            <ActivityDate
              name={data.activity_name}
              date={data.start}
              icon={(mainCategory?.icon ?? defaultIconByType) as TEventrIcons}
              iconColour={
                getCategoryProperties(data?.categories?.[0])
                  ? (getCategoryProperties(data?.categories?.[0])?.colour ?? "")
                  : (mainCategory?.colour ?? ColoursObj.core.secondary)
              }
            />
            {activityTimes}
            {activityExtraSection}
            <ActivityBookingInfo
              chatNames={[data.activity_name ?? ""]}
              attendess={data.attendees_list}
              attachments={attachments}
            />
          </>
        ) : null}
        <DraftMessage data={data} />
      </BaseCard>
    </ObjectProvider>
  );
});
